import React from 'react';
import { BarChart3, Layers, Zap, Cpu, ShieldCheck, Settings } from 'lucide-react';
import { motion } from 'framer-motion';

const WhyChooseSection = () => {
    const advantages = [
        { title: "Business-Oriented", icon: BarChart3, desc: "We build systems that generate revenue, not just design. Every line of code serves your business goals." },
        { title: "Scalable Architecture", icon: Layers, desc: "Future-ready structure—easy to upgrade and expand as your business grows." },
        { title: "Fast Delivery", icon: Zap, desc: "Agile methodology with milestone-based delivery ensuring you hit your deadlines." },
        { title: "Modern Tech Stack", icon: Cpu, desc: "We use the latest frameworks like MERN, Flutter, and Next.js for high-speed performance." },
        { title: "Transparent Pricing", icon: ShieldCheck, desc: "No hidden charges. Clear roadmap, timeline, and deliverables from day one." },
        { title: "Custom Admin Panels", icon: Settings, desc: "Complete control over your business operations with powerful custom back-office systems." },
    ];

    return (
        <section className="py-24 md:py-32 min-h-screen flex items-center bg-white relative transition-colors duration-500 overflow-hidden">
            {/* Visual Glows */}
            <div className="absolute top-1/2 left-1/4 w-[400px] h-[400px] bg-blue-500/5 rounded-full blur-[120px] -z-1" />

            <div className="container mx-auto px-4 md:px-6 relative z-10">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 0.6 }}
                    className="text-center mb-12 md:mb-16"
                >
                    <span className="text-primary font-black uppercase tracking-[0.4em] text-[10px] md:text-xs mb-3 block">The Advantage</span>
                    <h2 className="text-2xl md:text-4xl lg:text-5xl font-black text-slate-900 leading-tight tracking-tight">Why Choose <span className="text-gradient">MehareTech</span>?</h2>
                    <p className="text-slate-500 mt-4 max-w-2xl mx-auto text-xs md:text-lg leading-relaxed opacity-80 font-medium">We build systems that generate revenue, not just beautiful designs. Our focus is on your business growth.</p>
                </motion.div>

                <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8">
                    {advantages.map((item, idx) => (
                        <motion.div
                            key={idx}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.1 }}
                            transition={{ duration: 0.5, delay: idx * 0.05 }}
                            whileHover={window.innerWidth > 768 ? { y: -10 } : {}}
                            className="p-4 md:p-10 bg-slate-50 rounded-xl md:rounded-[2.5rem] border border-slate-100 hover:border-primary/30 transition-all group shadow-lg shadow-slate-200/50 overflow-hidden relative"
                        >
                            <div className="absolute top-0 right-0 w-20 h-20 bg-primary/5 rounded-full blur-3xl group-hover:bg-primary/10 transition-colors" />

                            <div className="w-8 h-8 md:w-16 md:h-16 bg-white text-primary rounded-lg md:rounded-2xl flex items-center justify-center mb-4 md:mb-8 shadow-md group-hover:bg-primary group-hover:text-white transition-all transform-gpu md:group-hover:rotate-6 relative z-10 border border-slate-100 group-hover:border-primary">
                                <item.icon className="w-4 h-4 md:w-10 md:h-10" strokeWidth={1.5} />
                            </div>
                            <h3 className="text-[13px] md:text-2xl font-black text-slate-900 mb-1.5 md:mb-3 relative z-10 group-hover:text-primary transition-colors tracking-tight leading-loose md:leading-tight">{item.title}</h3>
                            <p className="text-slate-500 text-[10px] md:text-base leading-tight md:leading-relaxed relative z-10 opacity-80 group-hover:text-slate-700 transition-colors line-clamp-2 md:line-clamp-none">{item.desc}</p>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default WhyChooseSection;
